<script>
    import { defineComponent } from 'vue';
    import MargettsApi from '../modules/margetts-api';

    export default defineComponent ({
        props: {
            endpoint: String,
            parameters: Object
        },

        data() {
            return {
                validationMessages: []
            }
        },

        methods: {
            callApi(object) {
                const querySting = object.parameters
                    ? this.createQueryStringFromParameters(this.convertObjectToParameters(object.parameters))
                    : '';

                if (object.confirm) {
                    if (! confirm(object.confirm)) {
                        return;
                    }
                }

                this.blockAll();

                MargettsApi.call(object.url, querySting, response => {
                    if (! response || ! response.result) {
                        return MargettsApi.throwNoResponseError();
                    }

                    if (response.result !== 'success') {
                        this.unblockAll();
                        return this.throwApiError(response.messages);
                    }

                    const data = response.data;

                    if (data.type && data.type === 'csv') {
                        this.downloadCsv(data.filename, data.body);
                    }

                    if (data.file) {
                        this.awaitFileInBackground(data.file.name, data.file.path);
                    }

                    if (response.messages && response.messages.length) {
                        flash(response.messages.join('<br>'), 'success');
                    }

                    if (data.refresh_table) {
                        this.refreshAll();
                    } else {
                        this.unblockAll();
                    }

                }, object.type, object.return);

            },

            convertObjectToParameters(object, includeUser = true) {
                const parameters = {};
                let key;

                for (key in object) {
                    if (object.hasOwnProperty(key)) {
                        parameters[key] = {
                            value: object[key]
                        };
                    }
                }

                if (includeUser) {
                    parameters['user'] = {
                        value: 'userId'
                    };
                }

                return parameters;
            },

            validateParameters(parameters) {
                this.validationMessages = [];
                this.validate(parameters);
                return ! this.validationMessages.length;
            },

            addValidationMessage(message) {
                this.validationMessages.push(message);
            },

            throwValidationError() {
                flash(this.validationMessages.join('<br>'), 'error');
                this.unblock();
            },

            createQueryStringFromParameters(parameters, useNestedValue = true) {
                if (! parameters) {
                    parameters = this.parameters;
                }

                let result = {};
                let key;

                for (key in parameters) {
                    if (parameters.hasOwnProperty(key)) {
                        if (key === 'user') {
                            result[key] = 'userId';
                        } else {
                            if (useNestedValue) {
                                if (parameters[key].value !== null) {
                                    result[key] = parameters[key].value;
                                }
                            } else {
                                if (parameters[key] !== null) {
                                    result[key] = parameters[key];
                                }
                            }
                        }
                    }
                }

                return new URLSearchParams(result);
            },

            isMissingRequiredParameters() {
                if (! this.endpoint) {
                    return true;
                }

                let result = false;
                let key;

                for (key in this.parameters) {
                    if (this.parameters.hasOwnProperty(key)) {
                        if (key !== 'user'
                            && this.parameters[key].required === true
                            && this.parameters[key].value === null
                        ) {
                            result = true;
                        }
                    }
                }

                return result;
            },

            updateParameter(name, value, shouldRenderOnChange = true) {
                if (! this.parameters[name]) {
                    this.parameters[name] = {};
                }

                // Loose check since we don't know what type of thing the value is
                const hasValueChanged = this.parameters[name].value
                    ? this.parameters[name].value != value
                    : (value && ! this.isFalsey(value));

                if (hasValueChanged) {
                    this.parameters[name].value = value;

                    if (shouldRenderOnChange) {
                        this.render();
                    }
                }
            },

            throwApiError(messages) {
                const message = typeof messages === 'string' ? messages : messages.join('<br>');
                MargettsApi.throwError('There was a problem: ' + message);
                this.unblock();
            },

            registerAsRefreshable() {
                window.events.on('refresh', () => {
                    this.render();
                });

                window.events.on('block', () => {
                    this.block();
                });

                window.events.on('unblock', () => {
                    this.unblock();
                });
            },

            refreshAll() {
                window.events.emit('refresh');
            },

            block() {
                this.isLoading = true;
            },

            unblock() {
                this.isLoading = false;
            },

            blockAll() {
                window.events.emit('block');
            },

            unblockAll() {
                window.events.emit('unblock');
            },

            registerParameterListener(name) {
                const eventName = 'update' + name.replace(/^\w/, c => c.toUpperCase());
                window.events.on(eventName, value => {
                    this.updateParameter(name, value);
                });
            },

            registerParameterListeners(names, exceptions = []) {
                [].forEach.call(names, name => {
                    if (! exceptions.includes(name)) {
                        this.registerParameterListener(name);
                    }
                });
            },

            isTruthy(value) {
                return value === 1 || value === '1' || value === true || value === 'true' || value === 'yes';
            },

            isFalsey(value) {
                return value === 0 || value === '0' || value === false || value === 'false' || value === 'no';
            },

            encodeHtml(html) {
                const parser = new DOMParser;
                const dom = parser.parseFromString('<!doctype html><html lang="en-gb"><body>' + html + '</body></html>', 'text/html');
                return dom.body.textContent;
            },

            generateDocumentLink(destination, forceDownload = false) {
                let path = 'https://' + this.domain + '/documents/';

                if (forceDownload) {
                    path = path + 'download/';
                }

                return path + destination.split('\\').join('/');
            },

            formatCell(cell) {
                let value;

                if (cell.type) {
                    if (cell.type === 'download' && cell.file) {
                        const destination = (cell.file.path ? cell.file.path.toString() : '') + (cell.file.name ? cell.file.name.toString() : '');

                        if (destination.indexOf('\\') > -1 ) {
                            return '<a href="' + this.generateDocumentLink(destination) + '" target="_blank" rel="noreferrer"><i class="fa-light fa-eye"></i></a>'
                            + '<a href="' + this.generateDocumentLink(destination, true) + '" class="show-on-large"><i class="fa-light fa-download"></i></a>';
                        }

                        return '<a href="' + destination + '" target="_blank" rel="noreferrer"><i class="fa-light fa-download"></i></a>';
                    }
                }

                if (cell.link) {
                    return this.retrieveLink(cell);
                }

                if (cell.media) {
                    return this.retrieveMedia(cell);
                }

                if (typeof cell.value === 'undefined') {
                    return '';
                }

                value = cell.value;

                if (cell.format) {
                    if (value == 0 && cell.format.zero) {
                        // Zero values need no further processing
                        return cell.format.zero;
                    }

                    if (cell.format.number_format) {
                        value = this.formatNumber(value, cell.format.number_format);
                    }

                    let icons = cell.format.icons ? cell.format.icons : '';

                    if (icons) {
                        let booleanValue = this.retrieveBooleanValue(value);

                        if (booleanValue === true) {
                            value = icons && icons[0].length > 0 ? '<i class="fa-light fa-' + icons[0] + '"></i>' : '';
                        }

                        if (booleanValue === false) {
                            value = icons && icons[1].length > 0 ? '<i class="fa-light fa-' + icons[1] + '"></i>' : '';
                        }

                        if (booleanValue === '') {
                            value = '';
                        }
                    }

                    if (cell.format.prepend) {
                        value = this.encodeHtml(cell.format.prepend) + value;
                    }

                    if (cell.format.append) {
                        value = value + this.encodeHtml(cell.format.append);
                    }

                }

                return value;
            },

            formatNumber(number, format) {
                if (format === 'currency') {
                    return number;
                }

                if (format === 'percent') {
                    return number;
                }

                return number;
            },

            retrieveBooleanValue(value) {
                if (this.isTruthy(value)) {
                    return true;
                }

                if (this.isFalsey(value)) {
                    return false;
                }

                return '';
            },

            retrieveAlignment(object) {
                return object.format && object.format.text_align ? object.format.text_align : null;
            },

            retrieveBackgroundColour(object) {
                if (typeof object.background_colour === 'string') {
                    return object.background_colour;
                }

                const backgroundColourObject = object.background_colour ? object.background_colour : null;

                if (backgroundColourObject === null) {
                    return '';
                }

                if (backgroundColourObject.rgba_value) {
                    return 'rgba(' + backgroundColourObject.rgba_value + ')';
                }

                return backgroundColourObject.hex_value;
            },

            retrieveBorderColour(object) {
                if (! object.format || ! object.format.border_colour) {
                    return null;
                }

                return object.format.border_colour;
            },

            retrieveBorderWidth(object) {
                if (! object.format || !object.format.border) {
                    return null;
                }

                if (object.format.border === 'all') {
                    return 'var(--border-width)';
                }

                const directions = ['top', 'right', 'bottom', 'left'];

                const widths = directions.map(direction => {
                    return direction === object.format.border ? 'var(--border-width)' : 0;
                });

                return widths.join(' ');
            },

            retrieveColour(object) {
                if (typeof object.colour === 'string') {
                    return object.colour;
                }

                if (! object.colour) {
                    return null;
                }

                if (object.colour.rgba_value) {
                    return 'rgba(' + object.colour.rgba_value + ')';
                }

                return object.colour.hex_value ? object.colour.hex_value : null;
            },

            retrieveContent(object) {
                if ( ! object.content) {
                    return null;
                }

                return {
                    show: object.content.value ? this.retrieveShow(object) : false,
                    alignment: this.retrieveAlignment(object.content),
                    fontStyle: this.retrieveFontStyle(object.content),
                    fontWeight: this.retrieveFontWeight(object.content),
                    text: object.content.value ? object.content.value : ''
                };
            },

            retrieveClasses(object, existingClassString = null) {
                const classes = existingClassString ? existingClassString.split(' ') : [];

                if (object.format) {
                    if (object.format.column_size) {
                        classes.push(object.format.column_size);
                    }

                    if (object.format.font_weight) {
                        classes.push(object.format.font_weight);
                    }
                }

                if (object.link && object.link.icon) {
                    classes.push('contains-button');
                }

                if (object.media) {
                    classes.push('media');
                }

                if (object.type) {
                    classes.push(object.type);
                }

                const classString = object.classes ? classes.concat(object.classes).join(' ').trim() : classes.join(' ').trim();

                return classString.length ? classString : null;
            },

            retrieveDisplay(object) {
                return object.display ? object.display : 'block';
            },

            retrieveFontSize(object) {
                return object.format && object.format.font_size ? object.format.font_size : null;
            },

            retrieveFontStyle(object) {
                return object.format && object.format.font_style ? object.format.font_style : null;
            },

            retrieveFontWeight(object) {
                return object.format && object.format.font_weight ? object.format.font_weight : null;
            },

            retrieveIconClass(object) {
                return 'fa-light fa-' + object.icon.name;
            },

            retrieveNextLevel(level) {
                return level + 1;
            },

            retrieveNextHeading(heading, isShowing) {
                return isShowing ? heading + 1 : heading;
            },

            retrieveLink(object) {
                if ( ! object.link) {
                    return null;
                }

                let link = '<a href="' + object.link.url + '"';

                if (object.link.icon) {
                    link += ' class="button" title="' + this.retrieveTooltip(object.link.icon) + '"';
                }

                if (object.link.target) {
                    link += ' target="' + object.link.target + '"';
                }

                link += '>';

                if (object.link.icon) {
                    link += '<i class="' + this.retrieveIconClass(object.link) + '"></i>';
                } else {
                    link += object.link.media ? this.retrieveMedia(object.link) : object.link.text;
                }

                link += '</a>';

                return link;
            },

            retrieveMedia(object) {
                if ( ! object.media) {
                    return null;
                }

                return '<img src="/api/resources/media?fileName=' + object.media.img + '&size=' + object.media.size + '" alt="' + object.media.alt + '" class="' + object.media.size + '">';
            },

            retrieveSections(object) {
                if ( ! object.sections) {
                    return null;
                }

                const sections = [];

                object.sections.forEach(section => {
                    const data = {
                        title: this.retrieveTitle(section),
                        content: this.retrieveContent(section),
                        format: section.format
                    };

                    if (section.table) {
                        data.table = section.table;
                    }

                    sections.push(data);
                });

                return sections;
            },

            retrieveShow(...objects) {
                return objects.filter(object => typeof object === 'object').filter(object => {
                    return object.display ? object.display !== 'none' : true;
                }).length > 0;
            },

            retrieveTextAreas(object) {
                if (! object.text_areas) {
                    return [];
                }

                const textAreas = [];

                [].forEach.call(object.text_areas, textArea => {
                    textAreas.push({
                        position: textArea.data.position,
                        title: this.retrieveTitle(textArea.data),
                        sections: this.retrieveSections(textArea.data),
                        format: textArea.data.format
                    });
                });

                return textAreas;
            },

            retrieveTitle(object) {
                if (! object.title) {
                    return null;
                }

                return {
                    show: this.retrieveShow(object.title),
                    alignment: this.retrieveAlignment(object.title),
                    colour: this.retrieveColour(object.title),
                    description: object.title.description ? object.title.description : '',
                    display: this.retrieveDisplay(object.title),
                    text: object.title.value ? object.title.value : '',
                    heading: object.title.heading ? object.title.heading : 3
                };
            },

            retrieveTooltip(object, shouldPrefix = false, prefixCondition = null) {
                const tooltip = object && object.tooltip ? object.tooltip : null;

                if (! tooltip) {
                    return '';
                }

                if (! shouldPrefix) {
                    return tooltip;
                }

                const prefix = prefixCondition ? 'Show ' : 'Hide ';
                return prefix + tooltip;
            },

            retrieveWidget(object) {
                if (object.text) {
                    return 'text';
                }

                return false;
            },

            downloadCsv(name, contents) {
                const csvAnchor = document.createElement('a');
                csvAnchor.href = 'data:text/csv;charset=utf-8,' + encodeURI(contents);
                csvAnchor.target = '_blank';
                csvAnchor.download = name;
                csvAnchor.click();
            },

            deselectInvestor(event) {
                this.block();

                axios.post('/actions/deselectInvestor').then(response => {
                    const data = response.data;

                    flash(data.message, 'success');

                    this.investor = null;
                    this.clientName = null;

                    this.unblock();
                });
            },

            awaitFileInBackground(name, path) {
                axios.post('/actions/awaitFileInBackground', {
                    name: name,
                    path: path
                }).then(response => {
                    //
                });
            }
        }
    })
</script>
